import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, User, Phone, Mail, MapPin, Building2, Home,
  Edit, Save, Loader2, PoundSterling, FileText, Calendar,
  CreditCard, Landmark, AlertCircle, CheckCircle, Send, Pen, Clock, RotateCcw,
  Users, Shield, BellOff, Settings, UserPlus, StickyNote, Plus, Trash2, AlertTriangle
} from "lucide-react";
import { API } from "@/App";
import { ApplicantForm } from "@/components/ApplicantForm";

export const LandlordDetailPage = () => {
  const { landlordId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [landlord, setLandlord] = useState(null);
  const [properties, setProperties] = useState([]);
  const [statements, setStatements] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Notes state
  const [notes, setNotes] = useState([]);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showEditNoteModal, setShowEditNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [newNoteIsAlert, setNewNoteIsAlert] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  
  // Bank accounts state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showBankAccountModal, setShowBankAccountModal] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [bankAccountForm, setBankAccountForm] = useState({
    account_name: "",
    bank_name: "",
    sort_code: "",
    account_number: "",
    is_primary: false,
    notes: ""
  });
  const [savingBankAccount, setSavingBankAccount] = useState(false);
  
  // Send LMA Modal
  const [showLMAModal, setShowLMAModal] = useState(false);
  const [sendingLMA, setSendingLMA] = useState(false);
  const [lmaData, setLmaData] = useState({
    property_id: "",
    property_address: "",
    service_level: "fully_managed",
    management_fee_percent: "",
    setup_fee: "",
    monthly_rent: "",
    // Landlord details (pre-filled, editable)
    landlord_name: "",
    landlord_email: "",
    landlord_phone: "",
    landlord_mobile: "",
    landlord_address: "",
    // Bank details
    bank_account_name: "",
    bank_name: "",
    bank_sort_code: "",
    bank_account_number: "",
    // Additional
    keys_supplied: "",
    special_instructions: ""
  });

  // Enhanced Applicant Details Modal
  const [showApplicantModal, setShowApplicantModal] = useState(false);
  const [savingApplicant, setSavingApplicant] = useState(false);
  const [applicantData, setApplicantData] = useState({
    is_business: false,
    business_name: "",
    contacts: [{
      title: "",
      first_name: "",
      last_name: "",
      friendly_name: "",
      gender: "",
      mobile_1: "",
      mobile_2: "",
      home_tel: "",
      work_tel: "",
      email_1: "",
      email_2: ""
    }],
    address: null,
    has_different_correspondence: false,
    correspondence_address: null,
    lead_staff: "",
    branches: [],
    consent_property_email: false,
    consent_property_sms: false,
    consent_marketing_email: false,
    consent_method: "",
    consent_notes: "",
    exclude_viewing_email: false,
    exclude_viewing_sms: false,
    exclude_appointment_email: false,
    exclude_appointment_sms: false,
    notes: ""
  });

  // Pre-fill LMA data when landlord loads
  useEffect(() => {
    if (landlord) {
      setLmaData(prev => ({
        ...prev,
        landlord_name: landlord.name || "",
        landlord_email: landlord.email || "",
        landlord_phone: landlord.phone || "",
        landlord_mobile: landlord.mobile || "",
        landlord_address: landlord.address || "",
        bank_account_name: landlord.bank_account_name || "",
        bank_name: landlord.bank_name || "",
        bank_sort_code: landlord.bank_sort_code || "",
        bank_account_number: landlord.bank_account_number || ""
      }));
      
      // Pre-fill applicant data
      if (landlord.applicant_data) {
        setApplicantData(landlord.applicant_data);
      } else {
        // Convert basic landlord info to applicant format
        const nameParts = (landlord.name || "").split(" ");
        setApplicantData(prev => ({
          ...prev,
          contacts: [{
            ...prev.contacts[0],
            first_name: nameParts[0] || "",
            last_name: nameParts.slice(1).join(" ") || "",
            email_1: landlord.email || "",
            mobile_1: landlord.phone || "",
            home_tel: landlord.phone || ""
          }],
          address: landlord.address ? { line_1: landlord.address } : null
        }));
      }
    }
  }, [landlord]);

  useEffect(() => {
    fetchData();
  }, [landlordId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [landlordRes, propertiesRes, statementsRes, documentsRes, notesRes] = await Promise.all([
        axios.get(`${API}/lettings/landlords/${landlordId}`, { headers }),
        axios.get(`${API}/lettings/properties?landlord_id=${landlordId}`, { headers }),
        axios.get(`${API}/rent/statements?landlord_id=${landlordId}&limit=10`, { headers }).catch(() => ({ data: { statements: [] } })),
        axios.get(`${API}/documents?recipient_id=${landlordId}&limit=10`, { headers }).catch(() => ({ data: { documents: [] } })),
        axios.get(`${API}/lettings/landlords/${landlordId}/notes`, { headers }).catch(() => ({ data: { notes: [] } }))
      ]);

      setLandlord(landlordRes.data);
      setProperties(propertiesRes.data.properties || []);
      setStatements(statementsRes.data.statements || []);
      setDocuments(documentsRes.data.documents || []);
      setNotes(notesRes.data.notes || []);
      setEditData(landlordRes.data);
      
      // Load bank accounts - either from array or convert legacy single account
      const existingAccounts = landlordRes.data.bank_accounts || [];
      if (existingAccounts.length === 0 && landlordRes.data.bank_account_name) {
        // Convert legacy single bank account to array format
        setBankAccounts([{
          id: "legacy_1",
          account_name: landlordRes.data.bank_account_name,
          bank_name: landlordRes.data.bank_name || "",
          sort_code: landlordRes.data.bank_sort_code || "",
          account_number: landlordRes.data.bank_account_number || "",
          is_primary: true,
          notes: ""
        }]);
      } else {
        setBankAccounts(existingAccounts);
      }
    } catch (error) {
      console.error("Error fetching landlord:", error);
      toast.error("Failed to load landlord details");
    } finally {
      setLoading(false);
    }
  };

  // Note handlers
  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;
    setSavingNote(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/lettings/landlords/${landlordId}/notes`, {
        content: newNoteContent,
        is_alert: newNoteIsAlert
      }, { headers });
      toast.success("Note added");
      setShowAddNoteModal(false);
      setNewNoteContent("");
      setNewNoteIsAlert(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleUpdateNote = async () => {
    if (!editingNote || !newNoteContent.trim()) return;
    setSavingNote(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/lettings/landlords/${landlordId}/notes/${editingNote.note_id}`, {
        content: newNoteContent,
        is_alert: newNoteIsAlert
      }, { headers });
      toast.success("Note updated");
      setShowEditNoteModal(false);
      setEditingNote(null);
      setNewNoteContent("");
      setNewNoteIsAlert(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm("Delete this note?")) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/lettings/landlords/${landlordId}/notes/${noteId}`, { headers });
      toast.success("Note deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  // Bank Account Handlers
  const handleSaveBankAccount = async () => {
    if (!bankAccountForm.account_name || !bankAccountForm.account_number) {
      toast.error("Account name and number are required");
      return;
    }
    setSavingBankAccount(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let updatedAccounts;
      
      if (editingBankAccount) {
        // Update existing account
        updatedAccounts = bankAccounts.map(acc => 
          acc.id === editingBankAccount.id 
            ? { ...bankAccountForm, id: acc.id }
            : (bankAccountForm.is_primary ? { ...acc, is_primary: false } : acc)
        );
      } else {
        // Add new account
        const newAccount = {
          ...bankAccountForm,
          id: `bank_${Date.now()}`
        };
        // If new account is primary, unset primary from others
        if (bankAccountForm.is_primary) {
          updatedAccounts = [...bankAccounts.map(acc => ({ ...acc, is_primary: false })), newAccount];
        } else {
          // If no accounts yet, make first one primary
          if (bankAccounts.length === 0) {
            newAccount.is_primary = true;
          }
          updatedAccounts = [...bankAccounts, newAccount];
        }
      }
      
      // Save to backend
      await axios.put(`${API}/lettings/landlords/${landlordId}`, {
        bank_accounts: updatedAccounts,
        // Also update legacy fields with primary account for backwards compatibility
        bank_account_name: updatedAccounts.find(a => a.is_primary)?.account_name || updatedAccounts[0]?.account_name || "",
        bank_name: updatedAccounts.find(a => a.is_primary)?.bank_name || updatedAccounts[0]?.bank_name || "",
        bank_sort_code: updatedAccounts.find(a => a.is_primary)?.sort_code || updatedAccounts[0]?.sort_code || "",
        bank_account_number: updatedAccounts.find(a => a.is_primary)?.account_number || updatedAccounts[0]?.account_number || ""
      }, { headers });
      
      setBankAccounts(updatedAccounts);
      toast.success(editingBankAccount ? "Bank account updated" : "Bank account added");
      setShowBankAccountModal(false);
      setEditingBankAccount(null);
      setBankAccountForm({
        account_name: "",
        bank_name: "",
        sort_code: "",
        account_number: "",
        is_primary: false,
        notes: ""
      });
    } catch (error) {
      toast.error("Failed to save bank account");
    } finally {
      setSavingBankAccount(false);
    }
  };

  const handleDeleteBankAccount = async (accountId) => {
    if (!confirm("Delete this bank account?")) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const updatedAccounts = bankAccounts.filter(acc => acc.id !== accountId);
      
      // If deleted account was primary and there are others, make first one primary
      if (updatedAccounts.length > 0 && !updatedAccounts.some(a => a.is_primary)) {
        updatedAccounts[0].is_primary = true;
      }
      
      await axios.put(`${API}/lettings/landlords/${landlordId}`, {
        bank_accounts: updatedAccounts,
        bank_account_name: updatedAccounts.find(a => a.is_primary)?.account_name || "",
        bank_name: updatedAccounts.find(a => a.is_primary)?.bank_name || "",
        bank_sort_code: updatedAccounts.find(a => a.is_primary)?.sort_code || "",
        bank_account_number: updatedAccounts.find(a => a.is_primary)?.account_number || ""
      }, { headers });
      
      setBankAccounts(updatedAccounts);
      toast.success("Bank account deleted");
    } catch (error) {
      toast.error("Failed to delete bank account");
    }
  };

  const handleSetPrimaryAccount = async (accountId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const updatedAccounts = bankAccounts.map(acc => ({
        ...acc,
        is_primary: acc.id === accountId
      }));
      
      const primaryAccount = updatedAccounts.find(a => a.is_primary);
      
      await axios.put(`${API}/lettings/landlords/${landlordId}`, {
        bank_accounts: updatedAccounts,
        bank_account_name: primaryAccount?.account_name || "",
        bank_name: primaryAccount?.bank_name || "",
        bank_sort_code: primaryAccount?.sort_code || "",
        bank_account_number: primaryAccount?.account_number || ""
      }, { headers });
      
      setBankAccounts(updatedAccounts);
      toast.success("Primary account updated");
    } catch (error) {
      toast.error("Failed to update primary account");
    }
  };

  const handleSaveApplicantDetails = async () => {
    setSavingApplicant(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build display name from contacts
      const contacts = applicantData.contacts || [];
      let displayName = landlord.name;
      if (contacts.length > 0 && contacts[0].first_name) {
        const names = contacts.map(c => {
          const title = c.title || "";
          return `${title} ${c.first_name} ${c.last_name}`.trim();
        });
        displayName = names.length === 1 ? names[0] : 
                      names.length === 2 ? `${names[0]} & ${names[1]}` :
                      names.slice(0, -1).join(", ") + ` & ${names[names.length - 1]}`;
      }
      
      // Update landlord with enhanced applicant data
      await axios.put(`${API}/lettings/landlords/${landlordId}`, {
        ...editData,
        name: displayName,
        email: contacts[0]?.email_1 || landlord.email,
        phone: contacts[0]?.mobile_1 || landlord.phone,
        applicant_data: applicantData
      }, { headers });
      
      toast.success("Landlord details updated");
      setShowApplicantModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save applicant details");
    } finally {
      setSavingApplicant(false);
    }
  };

  const handleSendLMA = async () => {
    if (!lmaData.property_id) {
      toast.error("Please select a property");
      return;
    }
    if (!landlord?.email) {
      toast.error("Landlord must have an email address");
      return;
    }

    setSendingLMA(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Send LMA with portal invite
      const response = await axios.post(`${API}/lettings/landlords/${landlordId}/send-lma`, {
        landlord_id: landlordId,
        property_id: lmaData.property_id,
        marketing_price: lmaData.monthly_rent ? parseFloat(lmaData.monthly_rent) : null,
        include_portal_invite: true
      }, { headers });

      toast.success("LMA sent to landlord's email with portal invite");
      
      if (response.data.temp_password) {
        toast.info(`Temp password: ${response.data.temp_password}`, { duration: 10000 });
      }
      
      setShowLMAModal(false);
      // Reset form but keep landlord pre-filled data
      setLmaData(prev => ({
        ...prev,
        property_id: "",
        property_address: "",
        management_fee_percent: "",
        setup_fee: "",
        monthly_rent: "",
        keys_supplied: "",
        special_instructions: ""
      }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send LMA");
    } finally {
      setSendingLMA(false);
    }
  };

  const handleResendDocument = async (documentId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/documents/${documentId}/resend`, {}, { headers });
      toast.success("Signing link resent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/lettings/landlords/${landlordId}`, editData, { headers });
      toast.success("Landlord details updated");
      setLandlord(editData);
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!landlord) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-lg font-medium">Landlord not found</p>
          <Button onClick={() => navigate("/admin/lettings")} className="mt-4">
            Back to Lettings
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">{landlord.name}</h1>
                <p className="text-xs text-muted-foreground">Landlord Profile</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {editing ? (
                <>
                  <Button variant="outline" onClick={() => { setEditing(false); setEditData(landlord); }}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setShowApplicantModal(true)}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Full Details
                  </Button>
                  <Button variant="outline" onClick={() => setShowLMAModal(true)}>
                    <Send className="w-4 h-4 mr-2" />
                    Send LMA
                  </Button>
                  <Button variant="outline" onClick={() => setEditing(true)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Quick Edit
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Contact Info */}
          <div className="space-y-6">
            {/* Contact Details Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  Contact Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div>
                      <Label>Full Name</Label>
                      <Input
                        value={editData.name || ""}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={editData.email || ""}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={editData.phone || ""}
                        onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Mobile</Label>
                      <Input
                        value={editData.mobile || ""}
                        onChange={(e) => setEditData({ ...editData, mobile: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Textarea
                        value={editData.address || ""}
                        onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                        rows={3}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3 py-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{landlord.email || "No email"}</span>
                    </div>
                    <div className="flex items-center gap-3 py-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{landlord.phone || "No phone"}</span>
                    </div>
                    {landlord.mobile && (
                      <div className="flex items-center gap-3 py-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{landlord.mobile} (Mobile)</span>
                      </div>
                    )}
                    <div className="flex items-start gap-3 py-2">
                      <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                      <span className="text-sm">{landlord.address || "No address"}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Bank Details Card */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Landmark className="w-5 h-5 text-emerald-600" />
                  Bank Accounts
                </CardTitle>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingBankAccount(null);
                    setBankAccountForm({
                      account_name: "",
                      bank_name: "",
                      sort_code: "",
                      account_number: "",
                      is_primary: bankAccounts.length === 0,
                      notes: ""
                    });
                    setShowBankAccountModal(true);
                  }}
                  data-testid="add-bank-account-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Account
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {bankAccounts.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No bank accounts on file. Click "Add Account" to add one.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {bankAccounts.map((account, index) => (
                      <div 
                        key={account.id || index}
                        className={`p-4 rounded-lg border ${account.is_primary ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="font-semibold">{account.account_name}</p>
                              {account.is_primary && (
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Primary</Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                              <div>
                                <span className="text-muted-foreground">Bank:</span>
                                <span className="ml-2 font-medium">{account.bank_name || "-"}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Sort Code:</span>
                                <span className="ml-2 font-medium font-mono">{account.sort_code || "-"}</span>
                              </div>
                              <div className="col-span-2">
                                <span className="text-muted-foreground">Account No:</span>
                                <span className="ml-2 font-medium font-mono">{account.account_number}</span>
                              </div>
                            </div>
                            {account.notes && (
                              <p className="text-xs text-muted-foreground mt-2 italic">{account.notes}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {!account.is_primary && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => handleSetPrimaryAccount(account.id)}
                                title="Set as primary"
                              >
                                <CheckCircle className="w-4 h-4 text-slate-400 hover:text-emerald-600" />
                              </Button>
                            )}
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => {
                                setEditingBankAccount(account);
                                setBankAccountForm({
                                  account_name: account.account_name || "",
                                  bank_name: account.bank_name || "",
                                  sort_code: account.sort_code || "",
                                  account_number: account.account_number || "",
                                  is_primary: account.is_primary || false,
                                  notes: account.notes || ""
                                });
                                setShowBankAccountModal(true);
                              }}
                              data-testid={`edit-bank-${account.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteBankAccount(account.id)}
                              data-testid={`delete-bank-${account.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tax Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Tax Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div>
                      <Label>Tax Reference / UTR</Label>
                      <Input
                        value={editData.tax_reference || ""}
                        onChange={(e) => setEditData({ ...editData, tax_reference: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>NI Number</Label>
                      <Input
                        value={editData.ni_number || ""}
                        onChange={(e) => setEditData({ ...editData, ni_number: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Company Name (if applicable)</Label>
                      <Input
                        value={editData.company_name || ""}
                        onChange={(e) => setEditData({ ...editData, company_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Company Registration No.</Label>
                      <Input
                        value={editData.company_reg_number || ""}
                        onChange={(e) => setEditData({ ...editData, company_reg_number: e.target.value })}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {landlord.tax_reference || landlord.company_name ? (
                      <>
                        {landlord.company_name && (
                          <div className="flex justify-between py-2">
                            <span className="text-sm text-muted-foreground">Company</span>
                            <span className="text-sm font-medium">{landlord.company_name}</span>
                          </div>
                        )}
                        {landlord.company_reg_number && (
                          <div className="flex justify-between py-2">
                            <span className="text-sm text-muted-foreground">Reg. No.</span>
                            <span className="text-sm font-medium">{landlord.company_reg_number}</span>
                          </div>
                        )}
                        {landlord.tax_reference && (
                          <div className="flex justify-between py-2">
                            <span className="text-sm text-muted-foreground">Tax Ref / UTR</span>
                            <span className="text-sm font-medium">{landlord.tax_reference}</span>
                          </div>
                        )}
                        {landlord.ni_number && (
                          <div className="flex justify-between py-2">
                            <span className="text-sm text-muted-foreground">NI Number</span>
                            <span className="text-sm font-medium">{landlord.ni_number}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No tax information on file
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Lead Management Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-amber-600" />
                  Lead Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div>
                      <Label>Lead Source</Label>
                      <Select
                        value={editData.lead_source || ""}
                        onValueChange={(v) => setEditData({ ...editData, lead_source: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rightmove">Rightmove</SelectItem>
                          <SelectItem value="zoopla">Zoopla</SelectItem>
                          <SelectItem value="website">Website</SelectItem>
                          <SelectItem value="referral">Referral</SelectItem>
                          <SelectItem value="walk_in">Walk In</SelectItem>
                          <SelectItem value="phone">Phone Enquiry</SelectItem>
                          <SelectItem value="existing_client">Existing Client</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Date Registered</Label>
                      <Input
                        type="date"
                        value={editData.date_registered || ""}
                        onChange={(e) => setEditData({ ...editData, date_registered: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Lead Staff</Label>
                      <Input
                        value={editData.lead_staff || ""}
                        onChange={(e) => setEditData({ ...editData, lead_staff: e.target.value })}
                        placeholder="Staff member name"
                      />
                    </div>
                    <div>
                      <Label>Preferred Contact Method</Label>
                      <Select
                        value={editData.preferred_contact_method || ""}
                        onValueChange={(v) => setEditData({ ...editData, preferred_contact_method: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="phone">Phone</SelectItem>
                          <SelectItem value="text">Text/SMS</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                ) : (
                  <>
                    {landlord.lead_source || landlord.date_registered ? (
                      <>
                        {landlord.lead_source && (
                          <div className="flex justify-between py-2">
                            <span className="text-sm text-muted-foreground">Source</span>
                            <Badge variant="outline" className="capitalize">{landlord.lead_source?.replace('_', ' ')}</Badge>
                          </div>
                        )}
                        {landlord.date_registered && (
                          <div className="flex justify-between py-2">
                            <span className="text-sm text-muted-foreground">Registered</span>
                            <span className="text-sm font-medium">{new Date(landlord.date_registered).toLocaleDateString('en-GB')}</span>
                          </div>
                        )}
                        {landlord.lead_staff && (
                          <div className="flex justify-between py-2">
                            <span className="text-sm text-muted-foreground">Lead Staff</span>
                            <span className="text-sm font-medium">{landlord.lead_staff}</span>
                          </div>
                        )}
                        {landlord.preferred_contact_method && (
                          <div className="flex justify-between py-2">
                            <span className="text-sm text-muted-foreground">Prefers</span>
                            <span className="text-sm font-medium capitalize">{landlord.preferred_contact_method}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No lead info recorded
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Notes Card */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <StickyNote className="w-5 h-5 text-amber-600" />
                  Notes
                </CardTitle>
                <Button
                  size="sm"
                  onClick={() => {
                    setNewNoteContent("");
                    setNewNoteIsAlert(false);
                    setShowAddNoteModal(true);
                  }}
                  data-testid="add-note-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Note
                </Button>
              </CardHeader>
              <CardContent>
                {notes.length > 0 ? (
                  <div className="space-y-3">
                    {notes.map((note) => (
                      <div 
                        key={note.note_id}
                        className={`p-3 rounded-lg border ${note.is_alert ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            {note.is_alert && (
                              <Badge variant="outline" className="mb-2 text-amber-600 border-amber-300 bg-amber-100">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Alert
                              </Badge>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              {note.created_by_name} • {new Date(note.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              {note.updated_at && ` (edited)`}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={() => {
                                setEditingNote(note);
                                setNewNoteContent(note.content);
                                setNewNoteIsAlert(note.is_alert);
                                setShowEditNoteModal(true);
                              }}
                              data-testid={`edit-note-${note.note_id}`}
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteNote(note.note_id)}
                              data-testid={`delete-note-${note.note_id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No notes yet. Click "Add Note" to create one.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Properties & Statements */}
          <div className="lg:col-span-2 space-y-6">
            {/* Properties */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Properties
                  <Badge variant="secondary">{properties.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {properties.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No properties linked</p>
                ) : (
                  <div className="space-y-3">
                    {properties.map(prop => (
                      <div 
                        key={prop.property_id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                        onClick={() => navigate(`/admin/lettings/property/${prop.property_id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Home className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{prop.address}</p>
                            <p className="text-sm text-muted-foreground">{prop.postcode}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {prop.monthly_rent ? (
                            <p className="font-medium text-emerald-600">£{prop.monthly_rent}/mo</p>
                          ) : null}
                          <Badge variant={prop.management_fee_percent ? "default" : "secondary"}>
                            {prop.management_fee_percent ? `${prop.management_fee_percent}% fee` : "Not set"}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Statements */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  Recent Statements
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statements.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No statements generated yet</p>
                ) : (
                  <div className="space-y-3">
                    {statements.map(stmt => (
                      <div 
                        key={stmt.statement_id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{stmt.period || "Statement"}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(stmt.created_at).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-emerald-600">£{stmt.net_amount?.toFixed(2)}</p>
                          <Badge variant={stmt.xero_synced ? "default" : "secondary"}>
                            {stmt.xero_synced ? "Synced" : "Pending"}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Documents (LMA, etc.) */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Pen className="w-5 h-5 text-purple-600" />
                    Documents
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setShowLMAModal(true)}>
                    <Send className="w-4 h-4 mr-2" />
                    Send LMA
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No documents sent yet</p>
                ) : (
                  <div className="space-y-3">
                    {documents.map(doc => (
                      <div 
                        key={doc.document_id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            doc.status === 'signed' ? 'bg-green-100' : doc.status === 'viewed' ? 'bg-amber-100' : 'bg-slate-100'
                          }`}>
                            {doc.status === 'signed' ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : doc.status === 'viewed' ? (
                              <Clock className="w-4 h-4 text-amber-600" />
                            ) : (
                              <Send className="w-4 h-4 text-slate-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">
                              {doc.document_type === 'lma' ? 'Landlord Management Agreement' : 'Tenancy Agreement'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {doc.property_address || 'No property'} • {new Date(doc.created_at).toLocaleDateString('en-GB')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={
                            doc.status === 'signed' ? 'default' : 
                            doc.status === 'viewed' ? 'secondary' : 'outline'
                          } className={doc.status === 'signed' ? 'bg-green-600' : ''}>
                            {doc.status === 'signed' ? 'Signed' : doc.status === 'viewed' ? 'Viewed' : 'Pending'}
                          </Badge>
                          {doc.status !== 'signed' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleResendDocument(doc.document_id)}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Financial Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PoundSterling className="w-5 h-5 text-amber-600" />
                  Financial Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">
                      £{properties.reduce((sum, p) => sum + (p.monthly_rent || 0), 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">Monthly Rent</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{properties.length}</p>
                    <p className="text-xs text-muted-foreground">Properties</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{statements.length}</p>
                    <p className="text-xs text-muted-foreground">Statements</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Send LMA Modal - Full Editor */}
      <Dialog open={showLMAModal} onOpenChange={setShowLMAModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Landlord Management Agreement
            </DialogTitle>
            <DialogDescription>
              Review and edit details before sending to {landlord?.name} for signature.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Property Selection */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <Label className="text-blue-800 font-semibold">Property *</Label>
              <Select
                value={lmaData.property_id}
                onValueChange={(v) => {
                  const prop = properties.find(p => p.property_id === v);
                  setLmaData({ 
                    ...lmaData, 
                    property_id: v,
                    property_address: prop?.address || "",
                    monthly_rent: prop?.monthly_rent || ""
                  });
                }}
              >
                <SelectTrigger className="mt-2 bg-white">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(prop => (
                    <SelectItem key={prop.property_id} value={prop.property_id}>
                      {prop.address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Landlord Details - Editable */}
            <div>
              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" />
                Landlord Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input
                    value={lmaData.landlord_name || ""}
                    onChange={(e) => setLmaData({ ...lmaData, landlord_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={lmaData.landlord_email || ""}
                    onChange={(e) => setLmaData({ ...lmaData, landlord_email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={lmaData.landlord_phone || ""}
                    onChange={(e) => setLmaData({ ...lmaData, landlord_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Mobile</Label>
                  <Input
                    value={lmaData.landlord_mobile || ""}
                    onChange={(e) => setLmaData({ ...lmaData, landlord_mobile: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Address</Label>
                  <Textarea
                    value={lmaData.landlord_address || ""}
                    onChange={(e) => setLmaData({ ...lmaData, landlord_address: e.target.value })}
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Bank Details - Editable */}
            <div>
              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Landmark className="w-4 h-4" />
                Bank Details (for rent payments)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Account Name</Label>
                  <Input
                    value={lmaData.bank_account_name || ""}
                    onChange={(e) => setLmaData({ ...lmaData, bank_account_name: e.target.value })}
                    placeholder="Name on account"
                  />
                </div>
                <div>
                  <Label>Bank Name</Label>
                  <Input
                    value={lmaData.bank_name || ""}
                    onChange={(e) => setLmaData({ ...lmaData, bank_name: e.target.value })}
                    placeholder="e.g. Barclays"
                  />
                </div>
                <div>
                  <Label>Sort Code</Label>
                  <Input
                    value={lmaData.bank_sort_code || ""}
                    onChange={(e) => setLmaData({ ...lmaData, bank_sort_code: e.target.value })}
                    placeholder="00-00-00"
                  />
                </div>
                <div>
                  <Label>Account Number</Label>
                  <Input
                    value={lmaData.bank_account_number || ""}
                    onChange={(e) => setLmaData({ ...lmaData, bank_account_number: e.target.value })}
                    placeholder="12345678"
                  />
                </div>
              </div>
            </div>

            {/* Service & Fees */}
            <div>
              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <PoundSterling className="w-4 h-4" />
                Service & Fees
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Service Level *</Label>
                  <Select
                    value={lmaData.service_level}
                    onValueChange={(v) => setLmaData({ ...lmaData, service_level: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="premium_managed">Premium Managed</SelectItem>
                      <SelectItem value="fully_managed">Fully Managed</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                      <SelectItem value="let_only">Tenant Find</SelectItem>
                      <SelectItem value="rent_collection">Rent Collection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Monthly Rent (£)</Label>
                  <Input
                    type="number"
                    value={lmaData.monthly_rent || ""}
                    onChange={(e) => setLmaData({ ...lmaData, monthly_rent: e.target.value })}
                    placeholder="e.g. 1200"
                  />
                </div>
                <div>
                  <Label>Management Fee (%) *</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={lmaData.management_fee_percent}
                    onChange={(e) => setLmaData({ ...lmaData, management_fee_percent: e.target.value })}
                    placeholder="e.g. 10"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Plus VAT at 20%</p>
                </div>
                <div>
                  <Label>Setup/Letting Fee (£)</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={lmaData.setup_fee}
                    onChange={(e) => setLmaData({ ...lmaData, setup_fee: e.target.value })}
                    placeholder="e.g. 200"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Plus VAT at 20%</p>
                </div>
              </div>
            </div>

            {/* Keys & Additional */}
            <div>
              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Home className="w-4 h-4" />
                Additional Details
              </h4>
              <div className="space-y-4">
                <div>
                  <Label>Keys Supplied</Label>
                  <Input
                    value={lmaData.keys_supplied || ""}
                    onChange={(e) => setLmaData({ ...lmaData, keys_supplied: e.target.value })}
                    placeholder="e.g. 3 sets - front door, back door"
                  />
                </div>
                <div>
                  <Label>Special Instructions / Notes</Label>
                  <Textarea
                    value={lmaData.special_instructions || ""}
                    onChange={(e) => setLmaData({ ...lmaData, special_instructions: e.target.value })}
                    rows={3}
                    placeholder="Any additional terms or notes for this agreement..."
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowLMAModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendLMA} disabled={sendingLMA}>
              {sendingLMA ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send for Signature
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhanced Applicant Details Modal */}
      <Dialog open={showApplicantModal} onOpenChange={setShowApplicantModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Full Landlord Details
            </DialogTitle>
            <DialogDescription>
              Edit comprehensive landlord information including contact details, consents, and preferences.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="details" className="mt-4">
            <TabsList className="w-full grid grid-cols-4">
              <TabsTrigger value="details">
                <User className="w-4 h-4 mr-2" />
                Details
              </TabsTrigger>
              <TabsTrigger value="status">
                <Settings className="w-4 h-4 mr-2" />
                Status
              </TabsTrigger>
              <TabsTrigger value="consents">
                <Shield className="w-4 h-4 mr-2" />
                Consents
              </TabsTrigger>
              <TabsTrigger value="reminders">
                <BellOff className="w-4 h-4 mr-2" />
                Reminders
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="mt-4">
              <ApplicantForm
                value={applicantData}
                onChange={setApplicantData}
                label="Landlord"
                showBusinessOption={true}
                showCorrespondenceAddress={true}
                showStatusFields={false}
                showMarketingConsents={false}
                showReminderExclusions={false}
              />
            </TabsContent>

            <TabsContent value="status" className="mt-4">
              <ApplicantForm
                value={applicantData}
                onChange={setApplicantData}
                label="Landlord"
                showBusinessOption={false}
                showCorrespondenceAddress={false}
                showStatusFields={true}
                showMarketingConsents={false}
                showReminderExclusions={false}
              />
            </TabsContent>

            <TabsContent value="consents" className="mt-4">
              <ApplicantForm
                value={applicantData}
                onChange={setApplicantData}
                label="Landlord"
                showBusinessOption={false}
                showCorrespondenceAddress={false}
                showStatusFields={false}
                showMarketingConsents={true}
                showReminderExclusions={false}
              />
            </TabsContent>

            <TabsContent value="reminders" className="mt-4">
              <ApplicantForm
                value={applicantData}
                onChange={setApplicantData}
                label="Landlord"
                showBusinessOption={false}
                showCorrespondenceAddress={false}
                showStatusFields={false}
                showMarketingConsents={false}
                showReminderExclusions={true}
              />
            </TabsContent>
          </Tabs>

          <DialogFooter className="border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => setShowApplicantModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveApplicantDetails} disabled={savingApplicant}>
              {savingApplicant ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Details
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Modal */}
      <Dialog open={showAddNoteModal} onOpenChange={setShowAddNoteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>Add a note for this landlord</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Note Content</Label>
              <Textarea
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                rows={4}
                placeholder="Enter your note..."
                data-testid="note-content-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is-alert"
                checked={newNoteIsAlert}
                onChange={(e) => setNewNoteIsAlert(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="is-alert" className="flex items-center gap-1 cursor-pointer">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Mark as Alert (highlighted)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddNoteModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddNote} disabled={savingNote || !newNoteContent.trim()}>
              {savingNote ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Plus className="w-4 h-4 mr-1" />
              )}
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Note Modal */}
      <Dialog open={showEditNoteModal} onOpenChange={setShowEditNoteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
            <DialogDescription>Update this note</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Note Content</Label>
              <Textarea
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                rows={4}
                placeholder="Enter your note..."
                data-testid="edit-note-content-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="edit-is-alert"
                checked={newNoteIsAlert}
                onChange={(e) => setNewNoteIsAlert(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="edit-is-alert" className="flex items-center gap-1 cursor-pointer">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Mark as Alert (highlighted)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditNoteModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateNote} disabled={savingNote || !newNoteContent.trim()}>
              {savingNote ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Account Modal */}
      <Dialog open={showBankAccountModal} onOpenChange={setShowBankAccountModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingBankAccount ? "Edit Bank Account" : "Add Bank Account"}</DialogTitle>
            <DialogDescription>
              {editingBankAccount ? "Update the bank account details" : "Add a new bank account for this landlord"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Account Holder Name *</Label>
              <Input
                value={bankAccountForm.account_name}
                onChange={(e) => setBankAccountForm({ ...bankAccountForm, account_name: e.target.value })}
                placeholder="e.g. Mr John Smith"
                data-testid="bank-account-name-input"
              />
            </div>
            <div>
              <Label>Bank Name</Label>
              <Input
                value={bankAccountForm.bank_name}
                onChange={(e) => setBankAccountForm({ ...bankAccountForm, bank_name: e.target.value })}
                placeholder="e.g. Barclays, HSBC, Lloyds"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sort Code</Label>
                <Input
                  value={bankAccountForm.sort_code}
                  onChange={(e) => setBankAccountForm({ ...bankAccountForm, sort_code: e.target.value })}
                  placeholder="00-00-00"
                />
              </div>
              <div>
                <Label>Account Number *</Label>
                <Input
                  value={bankAccountForm.account_number}
                  onChange={(e) => setBankAccountForm({ ...bankAccountForm, account_number: e.target.value })}
                  placeholder="12345678"
                  data-testid="bank-account-number-input"
                />
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={bankAccountForm.notes}
                onChange={(e) => setBankAccountForm({ ...bankAccountForm, notes: e.target.value })}
                placeholder="e.g. For property at 123 Main St only"
              />
            </div>
            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                id="is-primary-account"
                checked={bankAccountForm.is_primary}
                onChange={(e) => setBankAccountForm({ ...bankAccountForm, is_primary: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="is-primary-account" className="flex items-center gap-1 cursor-pointer">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Set as primary account (used for rent payments)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBankAccountModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveBankAccount} 
              disabled={savingBankAccount || !bankAccountForm.account_name || !bankAccountForm.account_number}
            >
              {savingBankAccount ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              {editingBankAccount ? "Update Account" : "Add Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandlordDetailPage;
